import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../../layouts/MainLayout';
import { Card, Button, Table, Tag, Space, Badge, message, Popconfirm, Tabs, Empty } from 'antd';
import { PlusOutlined, EditOutlined, EyeOutlined, TeamOutlined, ApartmentOutlined, DeleteOutlined, UnorderedListOutlined, CrownOutlined, FileExcelOutlined } from '@ant-design/icons';
import { designationsApi } from '../../../utils/api';
import { TableSkeleton } from '../../../components/SkeletonLoaders';
import StandardFilterPanel from '../../../components/hr/StandardFilterPanel';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { useListReturnState } from '../../../hooks/useListReturnState';

// تبدیل لیست تخت سمت‌ها به ساختار درختی برای نمایش سازمانی
const buildPositionTree = (list) => {
  if (!list?.length) return [];
  const byId = {};
  list.forEach((p) => {
    byId[p.id] = { ...p, children: [] };
  });
  const roots = [];
  list.forEach((p) => {
    const node = byId[p.id];
    const parentId = p.parent_id ?? p.parent?.id;
    if (parentId == null || !byId[parentId]) {
      roots.push(node);
    } else {
      byId[parentId].children.push(node);
    }
  });
  roots.forEach((r) => {
    r.children.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  });
  roots.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  return roots;
};

// کامپوننت نمایش یک گره در نمودار سازمانی سمت‌ها
const PositionOrgChartNode = ({ node, onNavigate, level = 0 }) => {
  const hasChildren = node.children && node.children.length > 0;
  const isRoot = level === 0;

  return (
    <div className="org-chart-node-wrapper" style={{ display: 'inline-block', verticalAlign: 'top' }}>
      <div className="org-chart-node-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div
          role="button"
          tabIndex={0}
          onClick={() => onNavigate(node.id)}
          onKeyDown={(e) => e.key === 'Enter' && onNavigate(node.id)}
          className={`org-chart-card ${isRoot ? 'org-chart-card--root' : 'org-chart-card--child'}`}
          style={{
            fontFamily: 'CustomFont',
            minWidth: isRoot ? 180 : 160,
            maxWidth: 220,
            padding: isRoot ? '14px 20px' : '10px 16px',
            borderRadius: 12,
            background: isRoot
              ? 'linear-gradient(135deg, #4c1d95 0%, #7c3aed 100%)'
              : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            color: isRoot ? '#fff' : '#1e293b',
            boxShadow: isRoot ? '0 4px 14px rgba(124, 58, 237, 0.4)' : '0 2px 8px rgba(0,0,0,0.08)',
            border: isRoot ? 'none' : '1px solid #e2e8f0',
            cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s',
            textAlign: 'center',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <CrownOutlined style={{ fontSize: isRoot ? 20 : 16, opacity: isRoot ? 1 : 0.8 }} />
            <span style={{ fontWeight: isRoot ? 700 : 600, fontSize: isRoot ? 15 : 14 }}>
              {node.name}
            </span>
          </div>
          {node.members_count != null && (
            <div style={{ fontSize: 11, marginTop: 4, opacity: 0.8 }}>
              {node.members_count} نفر
            </div>
          )}
        </div>
        {hasChildren && (
          <>
            <div
              className="org-chart-vline"
              style={{
                width: 2,
                minHeight: 24,
                background: 'linear-gradient(to bottom, #94a3b8, #cbd5e1)',
                borderRadius: 1,
              }}
            />
            <div
              className="org-chart-children"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 24,
                position: 'relative',
              }}
            >
              {node.children.map((child) => (
                <PositionOrgChartNode
                  key={child.id}
                  node={child}
                  onNavigate={onNavigate}
                  level={level + 1}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Positions = () => {
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = useResourcePermissions('employees');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [parentFilter, setParentFilter] = useState(null);
  const [parentOptions, setParentOptions] = useState([]);
  const [activeTab, setActiveTab] = useState('list');
  const [exporting, setExporting] = useState(false);
  const searchTimeoutRef = useRef(null);

  const captureCurrentListState = useCallback(
    () => ({
      searchText,
      parentFilter,
      activeTab,
    }),
    [searchText, parentFilter, activeTab],
  );

  const { navigateToDetail } = useListReturnState(captureCurrentListState, (restored) => {
    if (restored.searchText != null) setSearchText(restored.searchText);
    if (restored.parentFilter !== undefined) setParentFilter(restored.parentFilter);
    if (restored.activeTab) setActiveTab(restored.activeTab);
  });

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      await designationsApi.exportList({ parent_id: parentFilter != null ? parentFilter : 'all' });
      message.success('خروجی اکسل در حال دانلود است');
    } catch (error) {
      console.error('Export designations:', error);
      message.error(error?.message || 'خطا در خروجی اکسل');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    fetchData();
    loadParentOptions();
  }, []);

  const loadParentOptions = async () => {
    try {
      const res = await designationsApi.getAll();
      setParentOptions(res.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchData = async (query = '', selectedId = null) => {
    setLoading(true);
    try {
      const filters = {};
      
      // ✅ اگر ID انتخاب شده، فقط آن را بگیر
      if (selectedId) {
        filters.id = selectedId;
      }
      
      // ✅ اگر جستجو هست، بر اساس نام فیلتر کن
      if (query) {
        filters.name = query;
      }
      
      console.log('🔍 Fetching with filters:', filters);
      const res = await designationsApi.getAll(filters);
      setItems(res.data || []);
    } catch (e) {
      console.error('❌ Error fetching data:', e);
      message.error('خطا در دریافت سمت‌ها');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (value) => {
    setSearchText(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchData(value, parentFilter);
    }, 400);
  };

  const handleFilterChange = (key, value) => {
    if (key === 'parent') {
      setParentFilter(value);
      fetchData(searchText, value);
    }
  };

  const handleDelete = async (id) => {
    try {
      await designationsApi.delete(id);
      message.success('سمت با موفقیت حذف شد');
      fetchData(searchText, parentFilter);
    } catch (error) {
      console.error('خطا در حذف سمت:', error);
      message.error('خطا در حذف سمت');
    }
  };

  const columns = [
    {
      title: 'نام سمت',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <span
          className="text-gray-800 dark:text-gray-100 font-medium cursor-pointer hover:text-blue-600"
          onClick={() => navigateToDetail(`/hr/positions/${record.id}`)}
          style={{ fontFamily: 'CustomFont' }}
        >
          {text}
        </span>
      ),
    },
    {
      title: 'تعداد اعضا',
      dataIndex: 'members_count',
      key: 'members_count',
      width: 120,
      align: 'center',
      sorter: (a, b) => (a.members_count || 0) - (b.members_count || 0),
      render: (count) => (
        <Tag color={count > 0 ? 'blue' : 'default'} style={{ fontFamily: 'CustomFont' }}>
          {count || 0} نفر
        </Tag>
      ),
    },
    {
      title: 'سمت والد',
      dataIndex: 'parent',
      key: 'parent',
      render: (parent) =>
        parent ? (
          <Tag
            color="blue"
            icon={<TeamOutlined />}
            style={{
              fontFamily: 'CustomFont',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '2px 8px',
              lineHeight: 1.6,
            }}
          >
            {parent.name}
          </Tag>
        ) : (
          <Badge status="default" text={<span style={{ fontFamily: 'CustomFont', color: '#999' }}>بدون والد</span>} />
        ),
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => navigateToDetail(`/hr/positions/${record.id}`)}
            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
            style={{ fontFamily: 'CustomFont' }}
          />
          {canEdit ? (
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => navigateToDetail(`/hr/positions/${record.id}/edit`)}
              className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
              style={{ fontFamily: 'CustomFont' }}
            />
          ) : null}
          {canDelete ? (
            <Popconfirm
              title="حذف سمت"
              description="آیا از حذف این سمت اطمینان دارید؟"
              onConfirm={() => handleDelete(record.id)}
              okText="بله"
              cancelText="خیر"
              okButtonProps={{ danger: true }}
            >
              <Button
                type="text"
                icon={<DeleteOutlined />}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                style={{ fontFamily: 'CustomFont' }}
              />
            </Popconfirm>
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <div className="border-b border-gray-200 dark:border-gray-700 p-6 bg-white dark:bg-gray-800 rounded-t-lg">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center">
                  <ApartmentOutlined className="text-indigo-600 dark:text-indigo-400 text-xl" />
                </div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-0">مدیریت سمت‌های شغلی</h1>
              </div>
              <Space wrap>
                <Button
                  icon={<FileExcelOutlined />}
                  onClick={handleExportExcel}
                  loading={exporting}
                  style={{ fontFamily: 'CustomFont' }}
                >
                  خروجی اکسل
                </Button>
                {canAdd ? (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => navigate('/hr/positions/add')}
                    className="bg-green-600 hover:bg-green-700 border-0 shadow-md hover:shadow-lg transition-all"
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    افزودن سمت جدید
                  </Button>
                ) : null}
              </Space>
            </div>
          </div>

          <div className="p-6 bg-gray-50 dark:bg-gray-900/50">
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              size="middle"
              className="position-tabs"
              style={{ fontFamily: 'CustomFont' }}
              items={[
                {
                  key: 'list',
                  label: (
                    <span>
                      <UnorderedListOutlined style={{ marginLeft: 6 }} />
                      لیست سمت‌های شغلی
                    </span>
                  ),
                  children: (
                    <>
                      <StandardFilterPanel
                        searchValue={searchText}
                        onSearchChange={handleSearchChange}
                        searchPlaceholder="جستجو نام سمت..."
                        quickFilters={[
                          {
                            key: 'parent',
                            type: 'select',
                            label: 'سمت والد',
                            placeholder: 'انتخاب سمت خاص',
                            value: parentFilter,
                            options: parentOptions.map((p) => ({
                              value: p.id,
                              label: p.name,
                            })),
                            showSearch: true,
                            filterOption: (input, option) =>
                              (option?.children ?? '').toLowerCase().includes(input.toLowerCase()),
                          },
                        ]}
                        onFilterChange={handleFilterChange}
                        showTotalCount={true}
                        totalCount={items.length}
                      />

                      {loading ? (
                        <TableSkeleton rows={5} columns={3} />
                      ) : (
                        <Table
                          columns={columns}
                          dataSource={items}
                          rowKey="id"
                          loading={false}
                          pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            locale: { items_per_page: 'ردیف در صفحه' },
                          }}
                          locale={{
                            emptyText: (
                              <div style={{ fontFamily: 'CustomFont', padding: '32px 0', color: '#999' }}>
                                {searchText || parentFilter ? 'نتیجه‌ای یافت نشد' : 'سمتی ثبت نشده است'}
                              </div>
                            ),
                          }}
                          className="custom-table"
                          style={{ fontFamily: 'CustomFont' }}
                          rowClassName="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                        />
                      )}
                    </>
                  ),
                },
                {
                  key: 'chart',
                  label: (
                    <span>
                      <CrownOutlined style={{ marginLeft: 6 }} />
                      نمایش سازمانی
                    </span>
                  ),
                  children: (
                    <div className="org-chart-container positions-org-chart" style={{ overflowX: 'auto', padding: '24px 0', minHeight: 280 }}>
                      {loading ? (
                        <TableSkeleton rows={3} columns={2} />
                      ) : buildPositionTree(items).length === 0 ? (
                        <Empty
                          description={<span style={{ fontFamily: 'CustomFont' }}>سمتی برای نمایش وجود ندارد</span>}
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 32 }}>
                          {buildPositionTree(items).map((root) => (
                            <PositionOrgChartNode
                              key={root.id}
                              node={root}
                              onNavigate={(id) => navigateToDetail(`/hr/positions/${id}`)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  ),
                },
              ]}
            />
          </div>
        </Card>
      </div>
      <style jsx>{`
        :global(.positions-org-chart .org-chart-card:hover) {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(124, 58, 237, 0.25);
        }
        :global(.dark .positions-org-chart .org-chart-card--root) {
          background: linear-gradient(135deg, #3b0764 0%, #6d28d9 100%) !important;
          border-color: #334155;
        }
        :global(.dark .positions-org-chart .org-chart-card--child) {
          background: linear-gradient(135deg, #1e293b 0%, #334155 100%) !important;
          color: #e2e8f0;
          border-color: #475569;
        }
      `}</style>
    </MainLayout>
  );
};

export default Positions;